/**
 * v1.3.67 — Xtream catalog pre-warm hydration.
 *
 * v1.3.75 — ITVZA-inspired changes:
 *   • TTL skip: cold start renders straight from FS-cached bundle
 *     when fresh-enough (< 6 h old). NO network call at all.
 *   • Categories-first hydration: when there is NO disk cache, the
 *     first network call is the tiny `/categories` slice (~10 KB)
 *     so the UI can render the nav skeleton in ~150 ms. The heavy
 *     full bundle then arrives in the background and warms streams.
 *
 * On Capacitor Android, IndexedDB writes for the 4-5 MB bundle
 * round-trip through the WebView's IDB layer (~300-500 ms on a
 * mid-range Android TV). Capacitor Filesystem talks directly to the
 * Android file API → typical write/read of the same bundle is
 * ~30-50 ms.
 *
 * Why this matters
 * ----------------
 * The full bundle is ~5 MB compressed. On a slow upstream link this
 * is the difference between "tap app → 4 s blank → UI" and
 * "tap app → 150 ms → categories visible → tabs work".
 *
 * The request supports `If-None-Match` so up-to-date devices get
 * 304 Not Modified and we don't re-hydrate.
 */
import {
  cacheLiveCategories,
  cacheLiveStreams,
  cacheVodCategories,
  cacheVodStreams,
} from "./playlistCache";
import {
  fetchXtreamLiveStreams as _normLiveStreams,
  fetchXtreamVodStreams as _normVodStreams,
} from "./xtreamApi";
import { playerAxios } from "./playerApi";
// v1.3.71 — native disk cache for the bundle. Reading from Android
// Filesystem (~30 ms) is ~10× faster than re-downloading even when
// the server returns 304, and instant when offline.
import {
  readPrewarmBundleFromDisk,
  writePrewarmBundleToDisk,
  deletePrewarmBundleFromDisk,
} from "./prewarmFsCache";

// LocalStorage key for the last seen ETag — lets us send
// `If-None-Match` for instant 304 on unchanged bundles.
const ETAG_KEY = "vupp_xtream_prewarm_etag";

// v1.3.75 — ITVZA-style TTL. When the FS-cached bundle is younger
// than this, cold start does NOT contact the backend at all (not
// even a 304-check) — exactly the trick that makes ITVZA boot in
// ~150 ms with everything visible. The background refresh only
// fires after this window expires.
const COLD_START_TTL_MS = 6 * 60 * 60 * 1000; // 6 hours

function _etagKey(playlistId) {
  return `${ETAG_KEY}:${playlistId}`;
}

function _bundleAgeMs(bundle) {
  // The bundle carries `fetched_at` as epoch seconds (set on the
  // server when the harvest ran). Treat anything > 30 days as
  // bogus so a corrupt cache doesn't pin us to "always fresh".
  const t = Number(bundle?.fetched_at || 0) * 1000;
  if (!Number.isFinite(t) || t <= 0) return Infinity;
  const age = Date.now() - t;
  if (age < 0 || age > 30 * 24 * 60 * 60 * 1000) return Infinity;
  return age;
}

/**
 * Hydrate IDB from the server-side pre-warm bundle.
 *
 * @param {string} playlistId
 * @param {object} creds — `{ base, username, password, streamBase }`.
 *                        Used by the local normalisers to rebuild
 *                        stream URLs without a second network call.
 * @returns {Promise<{status, item_count?, fetched_at?, reason?, error?}>}
 *   • status:"hydrated" — bundle downloaded + IDB warmed
 *   • status:"fresh"    — server returned 304, IDB already current
 *   • status:"missing"  — server returned 404 (auto-trigger spawned)
 *   • status:"skipped"  — missing args (no creds / no playlist)
 *   • status:"error"    — network/parse failure (silent fallback)
 */
export async function hydrateFromPrewarm(playlistId, creds) {
  if (!playlistId || !creds) {
    return { status: "skipped", reason: "missing-args" };
  }
  const lsKey = _etagKey(playlistId);
  let lastEtag = "";
  try { lastEtag = localStorage.getItem(lsKey) || ""; } catch { /* ignore */ }

  // v1.3.71 — Fast path: if we have a bundle cached on native disk
  // AND the server hasn't published a new ETag, hydrate from disk
  // without ever hitting the network. Even when the server DOES have
  // a fresh bundle we still warm the IDB from disk first so the UI
  // renders immediately; the server fetch becomes a background
  // refresh.
  //
  // v1.3.75 — TTL skip: when the disk bundle is younger than
  // COLD_START_TTL_MS (6 h), DON'T fire the background refresh
  // either. Saves 1-2 s and ~5 MB of bandwidth on every cold start,
  // matches what ITVZA does on Smart TV.
  const diskBundle = await readPrewarmBundleFromDisk(playlistId);
  if (diskBundle && lastEtag) {
    // Hydrate IDB from disk in the background — don't await it on
    // the main thread; the UI will start rendering as soon as the
    // first category populates.
    _hydrateIdb(playlistId, diskBundle, creds).catch(() => {});
    const ageMs = _bundleAgeMs(diskBundle);
    const isFresh = ageMs < COLD_START_TTL_MS;
    if (!isFresh) {
      // Fire a 304-check in the background so a server-side refresh
      // gets picked up on the next launch. Best-effort; ignore errors.
      _backgroundRefresh(playlistId, lastEtag, creds).catch(() => {});
    }
    return {
      status: "hydrated-from-disk",
      reason: isFresh ? "fs-fresh-ttl-skip" : "fs-fast-path",
      bundle_etag: lastEtag,
      age_ms: Number.isFinite(ageMs) ? ageMs : null,
      ttl_skip: isFresh,
    };
  }

  // v1.3.75 — No disk cache: fire the small `/categories` slice
  // FIRST so the UI gets categories in ~150 ms, then fetch the full
  // bundle in the background to fill in streams. This matches the
  // "tap app → instantly see something" pattern of ITVZA.
  // Best-effort — failures fall through to the legacy full-bundle
  // path below (which is still the source of truth for streams).
  _hydrateCategoriesFirst(playlistId, creds).catch(() => {});

  // playerAxios attaches X-Device-ID + X-Activation-Key +
  // X-Platform + X-App-Version via its interceptor — we just add
  // the conditional ETag header.
  const headers = {};
  if (lastEtag) headers["If-None-Match"] = lastEtag;

  let res;
  try {
    res = await playerAxios.get(
      `/player/xtream-prewarm/${playlistId}`,
      {
        headers,
        // 304 must NOT be thrown — let us inspect status manually.
        validateStatus: (s) => (s >= 200 && s < 300) || s === 304 || s === 404,
        // Axios decompresses gzip automatically.
        responseType: "json",
      },
    );
  } catch (e) {
    return {
      status: "error",
      reason: "network",
      error: e?.message || String(e),
    };
  }
  if (res.status === 304) return { status: "fresh", reason: "etag-match" };
  if (res.status === 404) return { status: "missing", reason: "no-bundle" };
  if (res.status !== 200) {
    return { status: "error", reason: `http-${res.status}` };
  }

  const bundle = res.data;
  if (!bundle || typeof bundle !== "object") {
    return { status: "error", reason: "parse" };
  }
  const etag = res.headers?.etag || res.headers?.ETag || "";
  if (etag) {
    try { localStorage.setItem(lsKey, etag); } catch { /* ignore */ }
  }

  // v1.3.71 — persist to native disk for the next cold start.
  // Best-effort, doesn't block hydration.
  writePrewarmBundleToDisk(playlistId, bundle).catch(() => {});

  const itemCount = await _hydrateIdb(playlistId, bundle, creds);

  return {
    status: "hydrated",
    item_count: itemCount,
    fetched_at: bundle.fetched_at || null,
  };
}

/**
 * v1.3.75 — Categories-first hydration helper. Fires the tiny
 * `/categories` slice endpoint (~10 KB) so the player can render
 * its nav skeleton before the full streams bundle finishes
 * downloading. Best-effort; if this fails the full-bundle path
 * still runs and populates everything.
 */
async function _hydrateCategoriesFirst(playlistId, creds) {
  if (!playlistId || !creds) return;
  let res;
  try {
    res = await playerAxios.get(
      `/player/xtream-prewarm/${playlistId}/categories`,
      {
        validateStatus: (s) => (s >= 200 && s < 300) || s === 404,
        responseType: "json",
        // We want this to be quick; if it takes > 3 s the full
        // bundle is probably already on the way.
        timeout: 3000,
      },
    );
  } catch {
    return; // network failure — full bundle path will recover
  }
  if (res.status !== 200) return;
  const slice = res.data;
  if (!slice || typeof slice !== "object") return;
  // Only the category lists; streams come from the main bundle.
  if (Array.isArray(slice.live_categories) && slice.live_categories.length) {
    const cats = slice.live_categories.map((c) => ({
      category_id: String(c.category_id ?? ""),
      category_name: String(c.category_name ?? "Uncategorized"),
      parent_id: c.parent_id != null ? Number(c.parent_id) : 0,
    }));
    try { await cacheLiveCategories(playlistId, cats); } catch { /* noop */ }
  }
  if (Array.isArray(slice.vod_categories) && slice.vod_categories.length) {
    const cats = slice.vod_categories.map((c) => ({
      category_id: String(c.category_id ?? ""),
      category_name: String(c.category_name ?? "Uncategorized"),
      parent_id: c.parent_id != null ? Number(c.parent_id) : 0,
    }));
    try { await cacheVodCategories(playlistId, cats); } catch { /* noop */ }
  }
}

/**
 * v1.3.71 — actual IDB write loop, extracted so the fast-path
 * (disk cache hit) and the slow-path (network fetch) share one
 * implementation.
 */
async function _hydrateIdb(playlistId, bundle, creds) {
  // ----- Hydrate LIVE -----
  if (Array.isArray(bundle.live_categories)) {
    const cats = bundle.live_categories.map((c) => ({
      category_id: String(c.category_id ?? ""),
      category_name: String(c.category_name ?? "Uncategorized"),
      parent_id: c.parent_id != null ? Number(c.parent_id) : 0,
    }));
    await cacheLiveCategories(playlistId, cats);
  }
  if (bundle.live_streams_by_cat && typeof bundle.live_streams_by_cat === "object") {
    const cats = Array.isArray(bundle.live_categories) ? bundle.live_categories : [];
    const nameById = new Map(
      cats.map((c) => [String(c.category_id), c.category_name]),
    );
    for (const [catId, rawList] of Object.entries(bundle.live_streams_by_cat)) {
      if (!Array.isArray(rawList) || rawList.length === 0) continue;
      const stubFn = async () => JSON.stringify(rawList);
      const normalised = await _normLiveStreams(
        creds, catId, stubFn, nameById.get(catId),
      );
      if (Array.isArray(normalised) && normalised.length > 0) {
        await cacheLiveStreams(playlistId, catId, normalised);
      }
    }
  }
  // ----- Hydrate VOD -----
  if (Array.isArray(bundle.vod_categories)) {
    const cats = bundle.vod_categories.map((c) => ({
      category_id: String(c.category_id ?? ""),
      category_name: String(c.category_name ?? "Uncategorized"),
      parent_id: c.parent_id != null ? Number(c.parent_id) : 0,
    }));
    await cacheVodCategories(playlistId, cats);
  }
  if (bundle.vod_streams_by_cat && typeof bundle.vod_streams_by_cat === "object") {
    const cats = Array.isArray(bundle.vod_categories) ? bundle.vod_categories : [];
    const nameById = new Map(
      cats.map((c) => [String(c.category_id), c.category_name]),
    );
    for (const [catId, rawList] of Object.entries(bundle.vod_streams_by_cat)) {
      if (!Array.isArray(rawList) || rawList.length === 0) continue;
      const stubFn = async () => JSON.stringify(rawList);
      const normalised = await _normVodStreams(
        creds, catId, stubFn, nameById.get(catId),
      );
      if (Array.isArray(normalised) && normalised.length > 0) {
        await cacheVodStreams(playlistId, catId, normalised);
      }
    }
  }

  const itemCount =
    Object.values(bundle.live_streams_by_cat || {}).reduce(
      (n, arr) => n + (Array.isArray(arr) ? arr.length : 0), 0,
    ) +
    Object.values(bundle.vod_streams_by_cat || {}).reduce(
      (n, arr) => n + (Array.isArray(arr) ? arr.length : 0), 0,
    );
  return itemCount;
}

/**
 * v1.3.71 — background refresh after the fast-path served a stale
 * disk copy. If the server still returns 304 we just bail. If it
 * returns 200 with new data we overwrite both IDB and disk so the
 * NEXT app launch sees the fresh content.
 */
async function _backgroundRefresh(playlistId, etag, creds) {
  try {
    const res = await playerAxios.get(
      `/player/xtream-prewarm/${playlistId}`,
      {
        headers: etag ? { "If-None-Match": etag } : {},
        validateStatus: (s) => (s >= 200 && s < 300) || s === 304 || s === 404,
        responseType: "json",
      },
    );
    if (res.status !== 200) return; // 304 or 404 — nothing to do
    const newEtag = res.headers?.etag || res.headers?.ETag || "";
    if (newEtag === etag) return; // identical ETag — also a no-op
    if (newEtag) {
      try { localStorage.setItem(_etagKey(playlistId), newEtag); } catch { /* noop */ }
    }
    writePrewarmBundleToDisk(playlistId, res.data).catch(() => {});
    await _hydrateIdb(playlistId, res.data, creds);
  } catch { /* swallow — best-effort */ }
}

// Stale ESLint disable kept until I delete the dead unreachable code below.
// eslint-disable-next-line no-unused-vars
async function _legacyInlineHydrate_DEAD(_bundle, _playlistId, _creds) {
  // Replaced by _hydrateIdb above (v1.3.71). Kept as a no-op stub so
  // imports from older builds don't crash at runtime; safe to delete
  // in v1.3.72.
  return null;
}

/**
 * Suppress repeated hydration attempts inside the same browser
 * session. Once the IDB is warm there's no point hammering the
 * endpoint again on every render — the lazy refresh effects in
 * LiveTv.jsx / Browse.jsx already handle freshness.
 */
let _sessionPromise = null;
export function hydrateFromPrewarmOnce(playlistId, creds) {
  if (_sessionPromise) return _sessionPromise;
  _sessionPromise = hydrateFromPrewarm(playlistId, creds).finally(() => {
    // Allow another attempt after 5 minutes so a long-lived web
    // session can pick up a fresh server-side harvest.
    setTimeout(() => { _sessionPromise = null; }, 5 * 60 * 1000);
  });
  return _sessionPromise;
}

/** v1.3.71 — public helper for admin "Delete bundle" flow on this device. */
export function clearPrewarmDiskCache(playlistId) {
  return deletePrewarmBundleFromDisk(playlistId);
}
